<script setup lang="ts">
import HelpSlideover from "~/components/dashboard/slideovers/HelpSlideover.vue";
import NotificationsSlideover from "~/components/dashboard/slideovers/NotificationsSlideover.vue";
import UserDropdown from "~/components/dashboard/UserDropdown.vue";
import WorkspacesDropdown from "~/components/dashboard/ProjectsDropdown.vue";
import { useGetWorkspace } from "~/composables/workspaces/useGetWorkspace";
import { useGetProject } from "~/composables/projects/useGetProject";
import { useListProjects } from "~/composables/projects/useListProjects";
import { useFetchProfile } from "~/composables/auth/useFetchProfile";


const route = useRoute();
const { isHelpSlideoverOpen } = useDashboard();
const projectId = useCookie("project_id");
const { workspace } = useGetWorkspace();
const { project } = useGetProject();
const { projects, listProjects } = useListProjects();
const breadcrumbs = useBreadcrumbs();
const { user } = useFetchProfile();


const links = [
  {
    id: "home",
    label: "Home",
    icon: "i-heroicons-home",
    to: `/${workspace.value?.slug}/${project.value?.slug}`,
    tooltip: {
      text: "Home",
      shortcuts: ["G", "H"],
    },
  },
  {
    id: "judges",
    label: "Judges",
    icon: "i-mdi-robot",
    to: `/${workspace.value?.slug}/${project.value?.slug}/judges`,
    tooltip: {
      text: "Judges",
      shortcuts: ["G", "U"],
    },
  },
  {
    id: "events",
    label: "Events",
    icon: "i-heroicons-rss",
    to: `/${workspace.value?.slug}/${project.value?.slug}/events`,
    tooltip: {
      text: "Events",
      shortcuts: ["G", "U"],
    },
  },
  {
    id: "people",
    label: "People",
    icon: "i-heroicons-users",
    to: `/${workspace.value?.slug}/${project.value?.slug}/people`,
    tooltip: {
      text: "Users",
      shortcuts: ["G", "U"],
    },
  },
  {
    id: "groups",
    label: "Groups",
    icon: "i-heroicons-user-group",
    to: `/${workspace.value?.slug}/${project.value?.slug}/groups`,
    tooltip: {
      text: "Groups",
      shortcuts: ["G", "U"],
    },
  },
];

const resources = [
  {
    id: "queries",
    label: "Queries",
    to: `/${workspace.value?.slug}/${project.value?.slug}/queries`,
    badge: "10",
  },
  {
    id: "alerts",
    label: "Alerts",
    to: `/${workspace.value?.slug}/${project.value?.slug}/alerts`,
    badge: "6",
  },
  {
    id: "dashboards",
    label: "Dashboards",
    to: `/${workspace.value?.slug}/${project.value?.slug}/dashboards`,
    badge: "14",
  },
  {
    id: "notebooks",
    label: "Notebooks",
    to: `/${workspace.value?.slug}/${project.value?.slug}/notebooks`,
    badge: "6",
  },
];

const footerLinks = computed(() => {
  return [
    {
      label: "Invite teammates",
      icon: "i-heroicons-plus",
      to: `/${workspace.value?.slug}/settings/members`,
    },
    {
      id: "settings",
      label: "Settings",
      to: `/${workspace.value?.slug}/settings`,
      icon: "i-heroicons-cog-8-tooth",
      defaultOpen: false,
      children: [
        {
          label: "Workspace",
          to: `/${workspace.value?.slug}/settings`,
          exact: true,
        },
        {
          label: "Members",
          to: `/${workspace.value?.slug}/settings/members`,
        },
        {
          label: "API Keys",
          to: `/${workspace.value?.slug}/settings/api-keys`,
        },
        // {
        //   label: "Notifications",
        //   to: `/${workspace.value?.slug}/settings/notifications`,
        // },
        {
          id: "project-settings",
          label: "Project Settings",
          to: `/${workspace.value?.slug}/settings/${project.value.slug}`,
          defaultOpen: false,
          level: 1,
          children: [
            {
              label: "Overview",
              to: `/${workspace.value?.slug}/settings/${project.value.slug}`,
              exact: true,
            },
            {
              label: "Tokens",
              to: `/${workspace.value?.slug}/settings/${project.value.slug}/tokens`,
            },
            {
              label: "Members",
              to: `/${workspace.value?.slug}/settings/${project.value.slug}/members`,
            },
          ],
        },
      ],
    },
    {
      label: "Help & Support",
      icon: "i-heroicons-question-mark-circle",
      click: () => (isHelpSlideoverOpen.value = true),
    },
  ];
});

const groups = [
  {
    key: "projects",
    label: "Projects",
    commands: projects.value.map((p) => ({
      id: p.id,
      label: p.name,
      to: `/${workspace.value.slug}/${p.slug}`,
      avatar: { alt: p.name },
    })),
  },
  {
    key: "telemetry",
    label: "Telemetry",
    commands: links.map((link) => ({ ...link, shortcuts: link.tooltip?.shortcuts })),
  },
  {
    key: "resources",
    label: "Resources",
    commands: resources,
  },
  {
    key: "code",
    label: "Code",
    commands: [
      {
        id: "source",
        label: "View page source",
        icon: "i-simple-icons-github",
        click: () => {
          window.open(`https://github.com/nuxt-ui-pro/dashboard/blob/main/pages${route.path === "/" ? "/index" : route.path}.vue`, "_blank");
        },
      },
    ],
  },
];
</script>

<template>
  <UDashboardLayout>

    <UDashboardPanel :width="300" id="sidebar" :resizable="{ min: 150, max: 350 }" collapsible>
      <UDashboardNavbar class="!border-transparent" :ui="{ left: 'flex-1' }">
        <template #left>
          <WorkspacesDropdown />
        </template>
      </UDashboardNavbar>

      <UDashboardSidebar>
        <template #header>
          <UDashboardSearchButton />
        </template>

        <UDashboardSidebarLinks :links="links" />

        <UDivider />

        <UDashboardSidebarLinks :links="resources" />

        <div class="flex-1" />

        <UDashboardSidebarLinks :links="footerLinks" />

        <UDivider class="sticky bottom-0" />

        <template #footer>
          <UserDropdown />
        </template>
      </UDashboardSidebar>
    </UDashboardPanel>

    <UDashboardPanel grow>
      <UDashboardNavbar title="Settings">
        <template #title>
          <div>
            <UBreadcrumb :links="breadcrumbs" class="text-xs">
              <template #default="{ link, isActive, index }">
                <span class=text-xs>{{ link.label }}</span>
              </template>
            </UBreadcrumb>
            <div>{{ breadcrumbs[breadcrumbs.length - 1].label }}</div>
          </div>
        </template>

        <template #right>
          <slot name="right" />
        </template>

      </UDashboardNavbar>
      <slot />
    </UDashboardPanel>

    <HelpSlideover />
    <NotificationsSlideover />

    <ClientOnly>
      <LazyUDashboardSearch :groups="groups" />
    </ClientOnly>
  </UDashboardLayout>
</template>
